import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus } from 'lucide-react';
import { medicalRecordsApi, memedApi } from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { MemedExamList } from '@/components/memed/MemedExamList';

interface ExamRequest {
  id: string;
  medicalRecordId: string;
  patientId: string;
  doctorId: string;
  appointmentId: string;
  exams: Array<{ name: string; instructions?: string }>;
  priority: string;
  instructions?: string;
  isDigital: boolean;
  pdfUrl?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  memedId?: string;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED' | 'IDLE';
}

interface ExamRequestsResponse {
  exams: ExamRequest[];
  total: number;
  page: number;
  pageSize: number;
}

export function DoctorExamsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const doctorId = user?.id || '';
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'SYNCED' | 'PENDING' | 'FAILED'>('ALL');
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());

  const syncExamMutation = useMutation({
    mutationFn: async (examId: string) => {
      await memedApi.sendExamToMemed(examId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor', 'exams'] });
    },
    onMutate: (examId) => {
      setSyncingIds((prev) => new Set(prev).add(examId));
    },
    onSettled: (_, __, examId) => {
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.delete(examId);
        return next;
      });
    },
  });

  const resendExamMutation = useMutation({
    mutationFn: async (examId: string) => {
      await memedApi.sendExamToMemed(examId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor', 'exams'] });
    },
    onMutate: (examId) => {
      setResendingIds((prev) => new Set(prev).add(examId));
    },
    onSettled: (_, __, examId) => {
      setResendingIds((prev) => {
        const next = new Set(prev);
        next.delete(examId);
        return next;
      });
    },
  });

  const downloadPdfMutation = useMutation({
    mutationFn: async (examId: string) => {
      const result = await medicalRecordsApi.downloadExamRequestPdf(examId);
      const link = document.createElement('a');
      link.href = `data:${result.mimeType};base64,${result.pdf}`;
      link.download = `pedido_exame_${examId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  });

  const { data, isLoading, isError, refetch } = useQuery<ExamRequestsResponse>({
    queryKey: ['doctor', 'exams', page, filter],
    queryFn: async () => {
      const response = await medicalRecordsApi.getDoctorExamRequests(doctorId, page);
      return {
        exams: response.data as unknown as ExamRequest[],
        total: response.meta.total,
        page: response.meta.page,
        pageSize: response.meta.limit,
      };
    },
  } as any);

  const filteredExams = data?.exams.filter((exam) => {
    const sTerm = search.toLowerCase();
    const pName = ((exam as any).patientName || exam.patientId || '').toLowerCase();
    const matchesSearch = pName.includes(sTerm) ||
                          (exam.exams || []).some((e) => (e.name || '').toLowerCase().includes(sTerm));
    const matchesFilter =
      filter === 'ALL' ||
      (filter === 'SYNCED' && exam.memedStatus === 'SYNCED') ||
      (filter === 'PENDING' && exam.memedStatus === 'PENDING') ||
      (filter === 'FAILED' && exam.memedStatus === 'FAILED');
    return matchesSearch && matchesFilter;
  }) || [];

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Não informada';
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(dateStr));
    } catch {
      return 'Data inválida';
    }
  };

  const formatExams = (exams: any, fallbackType?: string) => {
    if (Array.isArray(exams) && exams.length > 0) {
      const names = exams
        .map((e) => (typeof e === 'string' ? e : e?.name || e?.examName || ''))
        .filter(Boolean);
      if (names.length > 0) return names;
    }
    if (fallbackType && typeof fallbackType === 'string' && fallbackType.trim()) {
      return fallbackType.split(',').map((t) => t.trim()).filter(Boolean);
    }
    return ['Exame Laboratorial / Imagem'];
  };

  const handleViewPdf = (examId: string) => {
    const exam = data?.exams?.find((e) => e.id === examId);
    if (exam?.pdfUrl) {
      window.open(exam.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    downloadPdfMutation.mutate(examId);
  };

  const handleResend = (examId: string) => {
    resendExamMutation.mutate(examId);
  };

  const handleSync = (examId: string) => {
    syncExamMutation.mutate(examId);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Pedidos de Exame</h1>
          <p className="text-sm text-muted-foreground">Consulte requisições emitidas e solicite novos exames laboratoriais ou de imagem</p>
        </div>
        <button
          onClick={() => navigate('/medico/exames/novo')}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary-hover shadow-xs transition-colors"
        >
          <Plus className="h-4 w-4" />
          Novo Pedido
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative flex-1 w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por paciente ou tipo de exame..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="w-full sm:w-auto px-4 py-2 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        >
          <option value="ALL">Todos os Status</option>
          <option value="SYNCED">Sincronizado</option>
          <option value="PENDING">Pendente</option>
          <option value="FAILED">Erro</option>
        </select>
      </div>

      {isLoading ? (
        <LoadingOverlay message="Carregando pedidos de exame..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar pedidos de exame" onRetry={() => refetch()} />
      ) : filteredExams.length === 0 ? (
        <EmptyState
          icon="FileText"
          title="Nenhum pedido de exame encontrado"
          description={search || filter !== 'ALL' ? 'Tente ajustar os filtros' : 'Você ainda não criou nenhum pedido de exame'}
          action={{ label: 'Criar Pedido de Exame', onClick: () => navigate('/medico/exames/novo') }}
        />
      ) : (
        <MemedExamList
          exams={filteredExams.map((exam) => ({
            id: exam.id,
            patientName: (exam as any).patientName || exam.patientId || 'Paciente',
            date: formatDate(exam.createdAt || (exam as any).requestedAt),
            exams: formatExams(exam.exams, (exam as any).type),
            memedStatus: exam.memedStatus || 'IDLE',
            memedId: exam.memedId,
            pdfUrl: exam.pdfUrl,
          }))}
          isLoading={isLoading}
          onViewPdf={handleViewPdf}
          onResend={handleResend}
          onSync={handleSync}
          syncingIds={syncingIds}
          resendingIds={resendingIds}
        />
      )}

      <div className="flex items-center justify-center gap-2 pt-4">
        <button
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          className="px-3 py-1.5 text-xs font-semibold border border-border bg-card text-foreground rounded-xl hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Anterior
        </button>
        <span className="px-3.5 py-1.5 text-xs font-bold bg-primary text-primary-foreground rounded-xl shadow-xs">{page}</span>
        <button
          onClick={() => setPage(page + 1)}
          disabled={page * 10 >= (data?.total || 0)}
          className="px-3 py-1.5 text-xs font-semibold border border-border bg-card text-foreground rounded-xl hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Próxima
        </button>
      </div>
    </div>
  );
}